/**
 * Authentication store using Zustand
 */
import { create } from "zustand";
import type { AuthState } from "./auth.types";
import {
  decodeToken,
  isTokenExpired,
  saveTokenToCookie,
  getTokenFromCookie,
  removeTokenFromCookie,
} from "./auth.utils";
import type { LoginResponse } from "../../api/client/types/auth.types";
import { RoleEnum } from "../../api/client/types/user.types";
import { queryClient } from "../../reactQueryClient";
import { clearAppSessionStorage } from "../../shared/utils/sessionStorageKeys";

interface AuthStore extends AuthState {
  // Actions
  setToken: (token: string) => void;
  preLogin: (response: LoginResponse) => void; // Set pre-authenticated state
  login: (response: LoginResponse) => void; // Set fully authenticated state
  logout: () => void;
  initialize: () => void;
}

/**
 * Determine authentication state from decoded token.
 * Returns tuple [isAuthenticated, isPreAuthenticated] with mutual exclusion.
 *
 * - Pre-authenticated: lifeguard with token but no buildingId
 * - Fully authenticated: admin with token, OR lifeguard with token and buildingId
 */
const determineAuthState = (
  decoded: ReturnType<typeof decodeToken>
): [boolean, boolean] => {
  if (!decoded) return [false, false];

  const isPreAuth = decoded.role === RoleEnum.LIFEGUARD && !decoded.building_id;
  const isFullyAuth =
    decoded.role === RoleEnum.ADMINISTRATOR ||
    (decoded.role === RoleEnum.LIFEGUARD && Boolean(decoded.building_id));

  // Mutual exclusion: can't be both true at the same time
  return [isFullyAuth, isPreAuth];
};

/**
 * Initialize auth state from token in cookie
 */
const initializeAuthState = (): Pick<
  AuthState,
  "token" | "user" | "isAuthenticated" | "isPreAuthenticated"
> => {
  const token = getTokenFromCookie();

  if (!token) {
    return {
      token: null,
      user: null,
      isAuthenticated: false,
      isPreAuthenticated: false,
    };
  }

  // Check if token is expired
  if (isTokenExpired(token)) {
    removeTokenFromCookie();
    return {
      token: null,
      user: null,
      isAuthenticated: false,
      isPreAuthenticated: false,
    };
  }

  // Decode token and extract user info
  const decoded = decodeToken(token);
  if (!decoded) {
    removeTokenFromCookie();
    return {
      token: null,
      user: null,
      isAuthenticated: false,
      isPreAuthenticated: false,
    };
  }

  const [isAuthenticated, isPreAuthenticated] = determineAuthState(decoded);

  return {
    token,
    user: {
      id: decoded.sub,
      role: decoded.role,
      buildingId: decoded.building_id,
    },
    isAuthenticated,
    isPreAuthenticated,
  };
};

/**
 * Authentication store
 */
export const useAuthStore = create<AuthStore>((set) => ({
  // Initial state
  token: null,
  user: null,
  isAuthenticated: false,
  isPreAuthenticated: false,
  isLoading: true,

  // Actions
  setToken: (token: string) => {
    if (isTokenExpired(token)) {
      set({
        token: null,
        user: null,
        isAuthenticated: false,
        isPreAuthenticated: false,
        isLoading: false,
      });
      removeTokenFromCookie();
      return;
    }

    const decoded = decodeToken(token);
    if (!decoded) {
      set({
        token: null,
        user: null,
        isAuthenticated: false,
        isPreAuthenticated: false,
        isLoading: false,
      });
      removeTokenFromCookie();
      return;
    }

    const [isAuthenticated, isPreAuthenticated] = determineAuthState(decoded);

    saveTokenToCookie(token);
    set({
      token,
      user: {
        id: decoded.sub,
        role: decoded.role,
        buildingId: decoded.building_id,
      },
      isAuthenticated,
      isPreAuthenticated,
      isLoading: false,
    });
  },

  preLogin: (response: LoginResponse) => {
    // Pre-login: sets pre-authenticated state (lifeguard with token but no buildingId)
    // setToken will automatically determine isPreAuthenticated = true, isAuthenticated = false
    const token = response.access_token;
    useAuthStore.getState().setToken(token);
  },

  login: (response: LoginResponse) => {
    // Full login: sets fully authenticated state
    // Admin: isAuthenticated = true, isPreAuthenticated = false
    // Lifeguard with buildingId: isAuthenticated = true, isPreAuthenticated = false
    // setToken will automatically determine the correct state
    clearAppSessionStorage();
    queryClient.clear();
    const token = response.access_token;
    useAuthStore.getState().setToken(token);
  },

  logout: () => {
    clearAppSessionStorage();
    queryClient.clear();
    removeTokenFromCookie();
    set({
      token: null,
      user: null,
      isAuthenticated: false,
      isPreAuthenticated: false,
      isLoading: false,
    });
  },

  initialize: () => {
    set({ isLoading: true });
    const state = initializeAuthState();
    set({ ...state, isLoading: false });
  },
}));

// Run initialization immediately on store load
useAuthStore.getState().initialize();
